<template>
  <div>
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>人事管理</el-breadcrumb-item>
      <el-breadcrumb-item>考勤配置</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 企微凭证卡片（secret 掩码回显，保存时掩码值不覆盖） -->
    <el-card class="box-card" style="margin-top: 20px;">
      <div slot="header" class="flexContainer">
        <span class="cardHeader">企微应用凭证
          <span class="card-tip">全系统唯一凭证源：免登 / 消息通知 / 考勤三链路共用；换 secret 或切企业在此修改即可，无需发版</span>
        </span>
        <el-button style="float:right;" type="primary" size="normal" @click="fetchAccount">刷新</el-button>
      </div>

      <el-form :model="accountForm" ref="accountForm" label-width="150px" size="medium">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="企业 corpid：">
              <el-input v-model="accountForm.corpid" placeholder="我的企业 → 企业信息 → 企业ID" clearable></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="自建应用 agentId：">
              <el-input v-model="accountForm.agentId" placeholder="应用管理 → 对应应用 → AgentId（消息通知用）" clearable></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="自建应用 Secret：">
              <el-input v-model="accountForm.appSecret" type="password" show-password :placeholder="secretPlaceholder('app')" clearable></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="通讯录同步 Secret：">
              <el-input v-model="accountForm.contactSecret" type="password" show-password :placeholder="secretPlaceholder('contact')" clearable></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item>
          <el-button type="primary" :loading="saving" @click="saveAccount">保存凭证</el-button>
          <span class="card-tip" style="margin-left: 12px;">已配置项回显 ******，留空或保持掩码不覆盖原值；保存后 token 缓存自动重置</span>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 手动操作卡片（同步通讯录 / 补拉日报 / 补拉打卡月报） -->
    <el-card class="box-card" style="margin-top: 20px;">
      <div slot="header" class="flexContainer">
        <span class="cardHeader">手动同步与历史补拉
          <span class="card-tip">异步受理，提交后看下方执行状态；补拉同日期段重复执行不会产生重复数据</span>
        </span>
      </div>

      <el-form label-width="150px" size="medium">
        <el-form-item label="同步通讯录：">
          <el-button type="primary" size="medium" :disabled="isRunning('syncUsers')" @click="triggerSync('syncUsers')">立即同步</el-button>
          <span class="card-tip" style="margin-left: 12px;">拉取企微通讯录成员（打卡数据的 userid 名单源，建议每天先跑）</span>
        </el-form-item>

        <el-form-item label="补拉日报：">
          <el-date-picker
            v-model="syncDayRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            size="medium"
            style="width: 300px;"
            :clearable="false"
            :picker-options="pickerOptions"
          ></el-date-picker>
          <el-button type="primary" size="medium" style="margin-left: 12px;" :disabled="isRunning('syncDay')" @click="triggerSyncDay">开始补拉</el-button>
          <span class="card-tip" style="margin-left: 12px;">单次范围上限 366 天（服务端自动按 30 天切片），适合历史回补</span>
        </el-form-item>

        <el-form-item label="补拉月报：">
          <el-date-picker
            v-model="syncMonthValue"
            type="month"
            value-format="yyyy-MM"
            placeholder="选择月份"
            size="medium"
            style="width: 160px;"
            clearable
            :picker-options="monthPickerOptions"
          ></el-date-picker>
          <el-button type="primary" size="medium" style="margin-left: 12px;" :disabled="isRunning('syncMonth')" @click="triggerSyncMonth">开始补拉</el-button>
          <span class="card-tip" style="margin-left: 12px;">拉取所选月份的全员月报</span>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 执行状态卡片（运行中自动轮询） -->
    <el-card class="box-card" style="margin-top: 20px;">
      <div slot="header" class="flexContainer">
        <span class="cardHeader">执行状态
          <span class="card-tip">内存态，服务重启后归零（worker 启动：{{ fmtTime(workerStartedAt) }}）</span>
        </span>
      </div>

      <el-table :data="taskRows" border size="medium">
        <el-table-column label="任务" width="150">
          <template slot-scope="scope">{{ scope.row.label }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="statusTagType(scope.row.state.status)" effect="dark">{{ statusText(scope.row.state.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="开始时间" width="160">
          <template slot-scope="scope">{{ fmtTime(scope.row.state.startedAt) }}</template>
        </el-table-column>
        <el-table-column label="结果" min-width="260" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.state.status === 'running'" class="card-tip">执行中…</span>
            <span v-else-if="scope.row.state.status === 'error'" class="error-text">{{ scope.row.state.message }}</span>
            <span v-else-if="scope.row.state.status === 'success'">{{ resultText(scope.row.key, scope.row.state) }}</span>
            <span v-else class="card-tip">{{ scope.row.state.message }}</span>
          </template>
        </el-table-column>
        <el-table-column label="完成时间" width="160">
          <template slot-scope="scope">{{ fmtTime(scope.row.state.finishedAt) }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 执行历史（落库日志，定时/手动全部留痕；排查定时漏拉看这里） -->
    <el-card class="box-card" style="margin-top: 20px;">
      <div slot="header" class="flexContainer">
        <span class="cardHeader">执行历史
          <span class="card-tip">全部执行记录（含定时调度与手动触发），服务重启不丢失</span>
        </span>
        <span>
          <el-select v-model="logFilterTask" placeholder="全部任务" clearable size="medium" style="width: 150px;" @change="handleLogFilterChange">
            <el-option v-for="(label, key) in taskLabels" :key="key" :label="label" :value="key"></el-option>
          </el-select>
          <el-button size="medium" style="margin-left: 8px;" @click="fetchSyncLogs">刷新</el-button>
        </span>
      </div>

      <el-table v-loading="logsLoading" :data="logList" border size="medium" max-height="420">
        <el-table-column label="时间" width="160">
          <template slot-scope="scope">{{ fmtTime(scope.row.startedAt) }}</template>
        </el-table-column>
        <el-table-column label="任务" width="130">
          <template slot-scope="scope">{{ taskLabels[scope.row.taskKey] || scope.row.taskKey }}</template>
        </el-table-column>
        <el-table-column label="来源" width="80" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="scope.row.source === 'schedule' ? 'info' : 'warning'" effect="plain">{{ scope.row.source === 'schedule' ? '定时' : '手动' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="scope.row.status === 'success' ? 'success' : 'danger'" effect="dark">{{ scope.row.status === 'success' ? '成功' : '失败' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="参数" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ logParamsText(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="结果 / 错误" min-width="260" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.status === 'error'" class="error-text">{{ scope.row.message }}</span>
            <span v-else>{{ logResultText(scope.row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="耗时" width="90" align="center">
          <template slot-scope="scope">{{ fmtDuration(scope.row.durationMs) }}</template>
        </el-table-column>
      </el-table>

      <el-pagination
        style="margin-top: 15px; text-align: right;"
        background
        layout="total, prev, pager, next"
        :total="logTotal"
        :current-page.sync="logPagenum"
        :page-size="10"
        @current-change="fetchSyncLogs"
      ></el-pagination>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'RenshiCheckinConfig',
  data () {
    // 补拉日期不可选未来
    const today = new Date()
    const fmtDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      // 凭证（secret 掩码回显）
      accountForm: {
        corpid: '',
        agentId: '',
        appSecret: '',
        contactSecret: ''
      },
      configured: { app: false, contact: false },
      saving: false,
      // 手动操作
      syncDayRange: [fmtDay(new Date(Date.now() - 6 * 86400000)), fmtDay(today)],
      syncMonthValue: '',
      // 执行状态（syncStatus 轮询）
      tasks: { syncUsers: { status: 'idle', message: '从未执行' }, syncDay: { status: 'idle', message: '从未执行' }, syncMonth: { status: 'idle', message: '从未执行' } },
      workerStartedAt: null,
      pollTimer: null,
      taskLabels: { syncUsers: '同步通讯录', syncDay: '补拉日报', syncMonth: '补拉打卡月报' },
      // 执行历史（落库日志，分页 + 任务筛选）
      logList: [],
      logTotal: 0,
      logPagenum: 1,
      logFilterTask: '',
      logsLoading: false,
      pickerOptions: {
        disabledDate (d) {
          return d.getTime() > today.getTime()
        }
      },
      monthPickerOptions: {
        disabledDate (d) {
          return d.getTime() > today.getTime()
        }
      }
    }
  },
  computed: {
    taskRows () {
      return Object.keys(this.taskLabels).map(key => ({
        key,
        label: this.taskLabels[key],
        state: this.tasks[key] || { status: 'idle', message: '从未执行' }
      }))
    }
  },
  created () {
    this.fetchAccount()
    this.fetchStatus()
    this.fetchSyncLogs()
  },
  beforeDestroy () {
    this.stopPolling()
  },
  methods: {
    /**
     * secret 输入框占位（已配置显示"已配置（******），输入新值覆盖"）
     */
    secretPlaceholder (kind) {
      return this.configured[kind] ? '已配置（留空不覆盖）' : '请输入 Secret'
    },
    async fetchAccount () {
      const { data: res } = await this.$http.get('renshi/checkin/account')
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
      this.accountForm.corpid = res.data.corpid || ''
      this.accountForm.agentId = res.data.agentId || ''
      this.accountForm.appSecret = res.data.appSecret || ''
      this.accountForm.contactSecret = res.data.contactSecret || ''
      this.configured.app = !!res.data.appSecret
      this.configured.contact = !!res.data.contactSecret
    },
    async saveAccount () {
      if (!this.accountForm.corpid.trim()) return this.$message.warning('请填写企业 corpid')
      if (!this.configured.app && !this.accountForm.appSecret) return this.$message.warning('请填写自建应用 Secret')
      this.saving = true
      try {
        const { data: res } = await this.$http.post('renshi/checkin/account', {
          corpid: this.accountForm.corpid.trim(),
          agentId: this.accountForm.agentId.trim(),
          appSecret: this.accountForm.appSecret || '',
          contactSecret: this.accountForm.contactSecret || ''
        })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.$message.success(res.meta.msg || '保存成功')
        this.fetchAccount()
      } finally {
        this.saving = false
      }
    },
    /**
     * 触发同步（异步受理 + 开启轮询）
     */
    async triggerSync (key, params) {
      const { data: res } = await this.$http.post(`renshi/checkin/${key}`, params || {})
      if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
      this.$message.success(res.meta.msg || '已受理')
      this.fetchStatus()
      this.startPolling()
    },
    async triggerSyncDay () {
      const [startDate, endDate] = this.syncDayRange || []
      if (!startDate || !endDate) return this.$message.warning('请选择补拉日期范围')
      await this.triggerSync('syncDay', { startDate, endDate })
    },
    async triggerSyncMonth () {
      if (!this.syncMonthValue) return this.$message.warning('请选择补拉月份')
      await this.triggerSync('syncMonth', { month: this.syncMonthValue })
    },
    isRunning (key) {
      return this.tasks[key] && this.tasks[key].status === 'running'
    },
    /**
     * 拉取执行状态；有任务运行中时持续轮询（5s）；从运行中转为全部空闲时顺带刷新执行历史
     */
    async fetchStatus () {
      const { data: res } = await this.$http.get('renshi/checkin/syncStatus')
      if (res.meta.status !== 200) return
      const wasRunning = Object.values(this.tasks).some(t => t.status === 'running')
      this.tasks = res.data.tasks || {}
      this.workerStartedAt = res.data.workerStartedAt
      const anyRunning = Object.values(this.tasks).some(t => t.status === 'running')
      if (anyRunning) this.startPolling()
      else {
        this.stopPolling()
        if (wasRunning) this.fetchSyncLogs() // 任务刚结束：刷新落库日志
      }
    },
    startPolling () {
      if (this.pollTimer) return
      this.pollTimer = setInterval(() => this.fetchStatus(), 5000)
    },
    stopPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    statusText (status) {
      return ({ running: '执行中', success: '完成', error: '失败', idle: '未执行' })[status] || status
    },
    statusTagType (status) {
      return ({ running: 'warning', success: 'success', error: 'danger', idle: 'info' })[status] || 'info'
    },
    /**
     * 成功结果摘要（按任务类型拼 stat 字段），执行状态与执行历史共用口径
     */
    resultText (key, state) {
      const s = state.stat || {}
      if (key === 'syncUsers') return `同步 ${s.total || 0} 名成员`
      if (key === 'syncDay') return `${s.dateRange || ''} · ${s.users || 0} 人 · 日报 ${s.dayUpserted || 0} 条`
      if (key === 'syncMonth') return `${s.month || ''} · ${s.users || 0} 人 · 打卡月报 ${s.upserted || 0} 条`
      return state.message
    },
    /**
     * 执行历史：拉取落库日志（分页 + 任务筛选）
     */
    async fetchSyncLogs () {
      this.logsLoading = true
      try {
        const params = { pagenum: this.logPagenum, pagesize: 10 }
        if (this.logFilterTask) params.taskKey = this.logFilterTask
        const { data: res } = await this.$http.get('renshi/checkin/syncLogs', { params })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.logList = res.data.list || []
        this.logTotal = res.data.total || 0
      } finally {
        this.logsLoading = false
      }
    },
    handleLogFilterChange () {
      this.logPagenum = 1
      this.fetchSyncLogs()
    },
    /**
     * 历史行参数摘要
     */
    logParamsText (row) {
      const p = row.params || {}
      if (row.taskKey === 'syncDay') return `${p.startDate || ''} ~ ${p.endDate || ''}`
      if (row.taskKey === 'syncMonth') return p.month || ''
      return '—'
    },
    /**
     * 历史行结果摘要（复用 resultText 口径）
     */
    logResultText (row) {
      return this.resultText(row.taskKey, { stat: row.stat, message: row.message })
    },
    fmtDuration (ms) {
      const n = Number(ms) || 0
      if (n < 1000) return `${n}ms`
      if (n < 60000) return `${(n / 1000).toFixed(1)}s`
      return `${Math.floor(n / 60000)}分${Math.round((n % 60000) / 1000)}秒`
    },
    fmtTime (v) {
      return v ? new Date(v).toLocaleString('sv-SE', { hour12: false }) : '—'
    }
  }
}
</script>

<style scoped>
.box-card {
  margin-bottom: 0;
}

.flexContainer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cardHeader {
  font-size: 16px;
  font-weight: bold;
}
.card-tip {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 8px;
}
.error-text {
  color: #F56C6C;
  font-size: 12px;
}
</style>
